/**
 * Loan Summary Service
 * Migrated from 3in1cms legacy/loanSummaryApi.ts
 */

import { request } from "../core/request";
import { API_RESPONSE_CODES, OPERATION_IDS } from "./constants";
import { offerAPI } from "./icors.service";
import type {
  ICallBackRequest,
  IDoacResposnse,
  IRawDoacResposnse,
  IRoiListItem,
  ITenoreListItem,
} from "../types/loanSummaryTypes";

const B2C_DOACVALIDATE_API_VERSION = "1.0";

/**
 * Get callback request
 * @param callBackRequest - Callback request data
 * @returns Callback response
 */
export const getCallBack = async (callBackRequest: ICallBackRequest) => {
  try {
    const response = await request({
      operationId: OPERATION_IDS.getACallBack,
      requestData: callBackRequest,
    });

    return response;
  } catch (error) {
    console.error("Error in getCallBack", error);
    throw error;
  }
};

/**
 * Get fallback ROI for PLCS product from ICORS
 * @returns Fallback ROI list
 */
const getFallBackRoiPLCS = async (): Promise<IRoiListItem[]> => {
  const [icorsResponse] = await offerAPI("PLCS");
  const fallbackRoi = icorsResponse?.pqOfferIrr
    ? [
        {
          loanAmtFrom: "0",
          loanAmtTo: "5000000",
          value: icorsResponse.pqOfferIrr,
          minTenor: 1,
          maxTenor: 96,
        },
      ]
    : [];
  return fallbackRoi;
};

/**
 * Get default tenor list
 * @returns Default tenor configuration
 */
const getDefaultTenorList = () => {
  const defaultTenor: ITenoreListItem = {
    minTenor: 1,
    maxTenor: 96,
    minLoanAmount: 0,
    maxLoanAmount: 5000000,
    allowedTenor: [36, 39, 42],
    pname: "",
  };
  return {
    selfemp: [defaultTenor],
    sal: [defaultTenor],
  };
};

/**
 * Get DOAC validate details
 * @param mobileNo - Mobile number
 * @param product - Product name
 * @returns DOAC response with cart, offers, ROI, and tenors
 */
export const getDoacValidateDetails = async (
  mobileNo: string,
  product: string
): Promise<IDoacResposnse> => {
  try {
    const response = (await request<
      IRawDoacResposnse,
      {
        mobileNumber: string;
        product: string;
        apiVersion: string;
      }
    >({
      operationId: OPERATION_IDS.doacqValidate,
      requestData: {
        mobileNumber: mobileNo,
        product,
        apiVersion: B2C_DOACVALIDATE_API_VERSION,
      },
    })) as IRawDoacResposnse;

    if (response.statusCode === API_RESPONSE_CODES.STATUS_CODE_7001) {
      const result: IDoacResposnse = {
        cartDetails: response.acqResponse.readFromCartResponse?.cartDetails || [],
        offerList: response.acqResponse.offersResponse?.offerList || [],
        roi:
          (response.acqResponse.roiResponse?.roi as IRoiListItem[]) ??
          (await getFallBackRoiPLCS()) ??
          [],
        ruralTenors:
          response.acqResponse.roiResponse?.ruralTenors?.sal?.length > 0
            ? response.acqResponse.roiResponse?.ruralTenors
            : getDefaultTenorList(),
        urbanTenors:
          response.acqResponse.roiResponse?.urbanTenors?.sal?.length > 0
            ? response.acqResponse.roiResponse?.urbanTenors
            : getDefaultTenorList(),
        productWiseTenorsList: response.acqResponse.roiResponse?.productWiseTenorsList || [],
      };

      return result;
    }

    throw new Error(JSON.stringify(response));
  } catch (error) {
    console.error("Error in getDoacValidateDetails", error);
    return {} as IDoacResposnse;
  }
};
